import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectMultiTouch,
} from "@talent/client-telemetry";
import { DOWN, LEFT, RIGHT, UP, canStep, type Maze } from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  easeOutCubic,
  glow,
  nudge,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * Magic maze — find the way out.
 *
 * The first activity where the score is the least interesting thing recorded. Almost every child
 * reaches the exit; what differs is how many steps it took against the shortest route, and how
 * often they walked back the way they came. That is the difference between looking before moving
 * and trying every corridor, and it is invisible in a right/wrong result.
 *
 * Both are recorded per trial. Neither becomes a claim about the child: planning is
 * descriptive-only in the Phase 0 triage, so this reports what happened and never what it means.
 *
 * **Controls are a D-pad, not cells.** A 6×6 maze on a 360 px phone gives ~54 px cells, under the
 * ~76 px a child's aim needs — so tapping cells would turn a mis-aimed tap into a wrong move.
 * Four big arrows are always the right size whatever the maze. Tapping an adjacent cell works too,
 * for the child who reaches for it, but nothing depends on hitting one.
 */

export interface MazeTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateMaze: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => Maze;
  stepBudget: (maze: Maze) => number;
  scoreTrial: (maze: Maze, reachedGoal: boolean, stepsTaken: number) => boolean;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

/** What one trial produced, beyond right or wrong. */
interface Walk {
  reachedGoal: boolean;
  steps: number;
  /** Moves that returned to the cell just left. "Doubling back", counted. */
  backtracks: number;
  /** Cells visited in order, so the route is re-drawable from the record. */
  path: number[];
}

export async function runMazeTask(opts: RunOptions): Promise<MazeTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // The play area is shared between games and must hold exactly one canvas.
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  const idle = (ms: number) => wait(ms, opts.signal);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    backdrop.circle(w / 2, h * 0.32, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /** The mouse. Drawn here rather than reused from the React creature, which is SVG. */
  function drawMouse(g: Graphics, r: number, color: number): void {
    g.clear();
    g.circle(-r * 0.62, -r * 0.66, r * 0.42).fill(mix(color, 0x000000, 0.2));
    g.circle(r * 0.62, -r * 0.66, r * 0.42).fill(mix(color, 0x000000, 0.2));
    g.circle(0, 0, r).fill(color);
    // Eyes, which is what makes it a creature and not a token.
    for (const dx of [-r * 0.34, r * 0.34]) {
      g.circle(dx, -r * 0.1, r * 0.2).fill(COLOR.ink);
      g.circle(dx + r * 0.07, -r * 0.17, r * 0.07).fill(0xffffff);
    }
    g.circle(0, r * 0.32, r * 0.13).fill(COLOR.wrong);
  }

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------
  /** Reserved for the D-pad. Fixed, so the maze cannot grow into the controls. */
  const PAD_HEIGHT = touchMin() * 3 + 24;

  let cell = 0;
  let boardX = 0;
  let boardY = 0;

  function measure(size: number): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    const available = Math.min(w * 0.92, h - PAD_HEIGHT - 48);
    cell = available / size;
    boardX = (w - cell * size) / 2;
    boardY = 24;
  }

  const centreOf = (index: number, size: number) => ({
    x: boardX + (index % size) * cell + cell / 2,
    y: boardY + Math.floor(index / size) * cell + cell / 2,
  });

  /**
   * Runs one maze and resolves with what the child did.
   *
   * The whole trial lives in this closure — its own graphics, its own listeners — so the next
   * trial starts from nothing rather than from whatever the last one left behind.
   */
  function present(maze: Maze): { walk: Promise<Walk>; cleanup: () => void } {
    const layer = new Container();
    stage.addChild(layer);
    measure(maze.size);

    const budget = opts.stepBudget(maze);
    const walls = new Graphics();
    const trail = new Graphics();
    const goalMark = new Graphics();
    const mouse = new Graphics();
    const meter = new Graphics();
    layer.addChild(walls, trail, goalMark, mouse, meter);

    // --- the maze itself ---
    const wallColor = mix(COLOR.stage, COLOR.card, 0.3);
    const drawWalls = () => {
    const thickness = Math.max(4, cell * 0.1);
    walls.clear();
    walls.roundRect(boardX - 6, boardY - 6, cell * maze.size + 12, cell * maze.size + 12, 18)
      .fill({ color: 0x000000, alpha: 0.18 });
    for (let i = 0; i < maze.size * maze.size; i++) {
      const x = boardX + (i % maze.size) * cell;
      const y = boardY + Math.floor(i / maze.size) * cell;
      const open = maze.open[i] ?? 0;
      // Only the top and left of each cell are drawn, plus the outer bottom and right edges —
      // otherwise every interior wall is drawn twice and the strokes double in weight.
      if ((open & UP) === 0) walls.moveTo(x, y).lineTo(x + cell, y).stroke({ width: thickness, color: wallColor, cap: "round" });
      if ((open & LEFT) === 0) walls.moveTo(x, y).lineTo(x, y + cell).stroke({ width: thickness, color: wallColor, cap: "round" });
      if (Math.floor(i / maze.size) === maze.size - 1 && (open & DOWN) === 0) {
        walls.moveTo(x, y + cell).lineTo(x + cell, y + cell).stroke({ width: thickness, color: wallColor, cap: "round" });
      }
      if (i % maze.size === maze.size - 1 && (open & RIGHT) === 0) {
        walls.moveTo(x + cell, y).lineTo(x + cell, y + cell).stroke({ width: thickness, color: wallColor, cap: "round" });
      }
    }
    walls.hitArea = {
      contains: (x: number, y: number) =>
        x >= boardX && y >= boardY && x <= boardX + cell * maze.size && y <= boardY + cell * maze.size,
    };
    };

    // --- the exit ---
    let goalReached = false;
    const drawGoal = () => {
      const goal = centreOf(maze.goal, maze.size);
      goalMark.clear();
      const r = cell * 0.3;
      const points: number[] = [];
      for (let i = 0; i < 10; i++) {
        const angle = (Math.PI / 5) * i - Math.PI / 2;
        const radius = i % 2 === 0 ? r : r * 0.44;
        points.push(goal.x + Math.cos(angle) * radius, goal.y + Math.sin(angle) * radius);
      }
      goalMark.poly(points).fill(goalReached ? COLOR.correct : COLOR.sunny);
    };

    // --- state ---
    let at = maze.start;
    const path: number[] = [maze.start];
    let backtracks = 0;
    let accepting = true;
    let resolveWalk: ((w: Walk) => void) | null = null;

    const placeMouse = () => {
      const p = centreOf(at, maze.size);
      mouse.position.set(p.x, p.y);
      drawMouse(mouse, cell * 0.3, lighten(accent, 0.2));
    };

    /** The route walked so far, so a child can see where they have been. */
    const drawTrail = () => {
      trail.clear();
      if (path.length < 2) return;
      const first = centreOf(path[0] as number, maze.size);
      trail.moveTo(first.x, first.y);
      for (const c of path.slice(1)) {
        const p = centreOf(c, maze.size);
        trail.lineTo(p.x, p.y);
      }
      trail.stroke({ width: cell * 0.14, color: accent, alpha: 0.35, cap: "round", join: "round" });
    };

    /**
     * Steps remaining, as a ring that empties.
     *
     * A ring and not a numeral: a number is text (v2 §15), and it also tells a child they are
     * running out in a way a shape does not.
     *
     * It sits in the middle of the D-pad, which is dead space between the four arrows and is
     * exactly where a child's eyes and thumb already are. A bar under the maze had nowhere to go —
     * on a 640 px screen the gap between the maze and the arrows is under thirty pixels.
     */
    const drawMeter = () => {
      const cx = app.renderer.width / 2;
      const cy = app.renderer.height - PAD_HEIGHT / 2 - 8;
      const radius = touchMin() * 0.38;
      const width = 8;
      const steps = path.length - 1;
      const left = Math.max(0, 1 - steps / budget);

      meter.clear();
      meter.circle(cx, cy, radius).stroke({ width, color: 0xffffff, alpha: 0.14 });
      if (left > 0) {
        // Sweeps clockwise from twelve o'clock. `moveTo` first, or the arc is joined to wherever
        // the path last was — which draws a stray line across the screen.
        const from = -Math.PI / 2;
        const to = from + Math.PI * 2 * left;
        meter
          .moveTo(cx + Math.cos(from) * radius, cy + Math.sin(from) * radius)
          .arc(cx, cy, radius, from, to)
          .stroke({
            width,
            // Turns warm as it empties, which is a warning read without words.
            color: left > 0.35 ? accent : COLOR.sunny,
            cap: "round",
          });
      }
    };

    const finish = (won: boolean) => {
      accepting = false;
      resolveWalk?.({ reachedGoal: won, steps: path.length - 1, backtracks, path: [...path] });
    };

    /** One move, if the wall allows it. */
    const move = (dir: number) => {
      if (!accepting) return;
      const next = canStep(maze, at, dir);
      if (next === null) {
        // A wall. Acknowledged rather than ignored — an unanswered tap reads as a broken button,
        // and the child taps again, which the contamination detectors see as an anomaly.
        tick();
        void nudge(mouse, 4);
        return;
      }
      // A move back to the cell just left is the signal this game exists to capture.
      if (path.length >= 2 && next === path[path.length - 2]) backtracks++;

      at = next;
      path.push(next);
      cellTone(next % 9);
      const p = centreOf(next, maze.size);
      ripple(fx, p.x, p.y, accent, cell * 0.7);
      drawTrail();
      drawMeter();

      if (prefersReducedMotion()) {
        placeMouse();
      } else {
        const from = { x: mouse.position.x, y: mouse.position.y };
        void animate(140, (t) => {
          const e = easeOutCubic(t);
          mouse.position.set(from.x + (p.x - from.x) * e, from.y + (p.y - from.y) * e);
        });
      }

      if (next === maze.goal) {
        finish(true);
      } else if (path.length - 1 >= budget) {
        finish(false);
      }
    };

    // --- D-pad ---
    const button = touchMin();
    const arrows: { dir: number; dx: number; dy: number; rotation: number }[] = [
      { dir: UP, dx: 0, dy: -button, rotation: 0 },
      { dir: DOWN, dx: 0, dy: button, rotation: Math.PI },
      { dir: LEFT, dx: -button, dy: 0, rotation: -Math.PI / 2 },
      { dir: RIGHT, dx: button, dy: 0, rotation: Math.PI / 2 },
    ];

    const pad = arrows.map((arrow) => {
      const g = new Graphics();
      g.roundRect(-button / 2, -button / 2, button, button, 20).fill({
        color: 0xffffff,
        alpha: 0.12,
      });
      // A triangle, rotated. Language-free, and unambiguous in a way a glyph is not — `›` and
      // friends are bidi-mirrored inside an RTL document.
      const t = button * 0.2;
      g.poly([0, -t, t, t * 0.7, -t, t * 0.7]).fill(0xffffff);
      g.rotation = arrow.rotation;
      g.eventMode = "static";
      g.cursor = "pointer";
      const half = button / 2 + 6;
      g.hitArea = {
        contains: (x: number, y: number) => x >= -half && y >= -half && x <= half && y <= half,
      };
      g.on("pointertap", () => move(arrow.dir));
      layer.addChild(g);
      return { g, arrow };
    });

    /**
     * Positions and redraws everything from the CURRENT canvas size.
     *
     * On a phone the visual viewport changes whenever the address bar slides away, which fires a
     * renderer resize. The first version measured once at trial start, so after any such change
     * the maze was drawn for a screen that no longer existed and the D-pad sat off the bottom.
     */
    function layout(): void {
      measure(maze.size);
      const padCentreX = app.renderer.width / 2;
      const padCentreY = app.renderer.height - PAD_HEIGHT / 2 - 8;
      for (const { g, arrow } of pad) {
        g.position.set(padCentreX + arrow.dx, padCentreY + arrow.dy);
      }
      drawWalls();
      drawGoal();
      drawTrail();
      drawMeter();
      placeMouse();
    }

    // Tapping an adjacent cell works too, for the child who reaches for the maze itself. Nothing
    // depends on it, so a small cell costs nothing.
    walls.eventMode = "static";
    walls.on("pointertap", (event) => {
      if (!accepting) return;
      const local = stage.toLocal(event.global);
      const col = Math.floor((local.x - boardX) / cell);
      const row = Math.floor((local.y - boardY) / cell);
      const target = row * maze.size + col;
      for (const dir of [UP, RIGHT, DOWN, LEFT]) {
        if (canStep(maze, at, dir) === target) {
          move(dir);
          return;
        }
      }
    });

    layout();
    /**
     * The pieces arrive rather than appearing.
     *
     * A board that is simply there reads as a diagram; one that is dealt out reads as a game being set
     * up for you — and it gives a child a moment to look before anything is expected of them. Staggered
     * so it is a sequence, and skipped under `prefers-reduced-motion`, where the whole point of the
     * setting is that things do not fly around.
     */
    if (!prefersReducedMotion()) {
      // The maze as one piece rather than cell by cell: a corridor that assembles itself is harder to
      // read than a map that lands, and the child has to see the whole route to plan one.
      for (const part of [walls, goalMark, mouse]) {
        part.scale.set(0);
      }
      void animate(300, (t) => {
        const s = easeOutBack(t);
        walls.scale.set(s);
        goalMark.scale.set(s);
        mouse.scale.set(s);
      });
    }

    app.renderer.on("resize", layout);
    const goalPoint = centreOf(maze.goal, maze.size);
    glow(glowLayer, goalPoint.x, goalPoint.y, cell * 0.6, COLOR.sunny, 1400);

    if (!prefersReducedMotion()) {
      layer.alpha = 0;
      void animate(300, (t) => (layer.alpha = easeOutBack(Math.min(1, t))));
    }

    const walk = new Promise<Walk>((resolve) => {
      resolveWalk = resolve;
    }).then(async (result) => {
      const goal = centreOf(maze.goal, maze.size);
      goalReached = true;
      drawGoal();
      if (result.reachedGoal) {
        successChime();
        burst(fx, goal.x, goal.y, [accent, lighten(accent, 0.4), COLOR.sunny], 24);
        await idle(700);
      } else {
        missTone();
        await nudge(mouse);
        // Show the way out rather than only ending the trial. Running out of steps is not a
        // verdict about the child, and a maze that closes without showing the answer teaches
        // nothing.
        trail.clear();
        glow(glowLayer, goal.x, goal.y, cell * 0.7, COLOR.correct, 900);
        await idle(900);
      }
      return result;
    });

    return {
      walk,
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: MazeTaskResult["reason"] = "completed";

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const maze = opts.generateMaze(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The maze itself, so the trial is re-drawable and re-scorable from the record alone:
        // the wall bitmasks, then the start and the goal.
        stimulus: [...maze.open, maze.start, maze.goal],
      });

      const start = performance.now();
      const trial = present(maze);
      const outcome = await untilSettled(trial.walk, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(maze, outcome.reachedGoal, outcome.steps);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        // The route walked. Kept whole rather than reduced to a step count, because the shape of
        // a wrong route is the interesting part — a child who explored one branch deeply and a
        // child who dithered at every junction take the same number of steps.
        response: outcome.path,
        correct,
        responseMs: elapsed,
        // Doubling back: self-correction, and the clearest available signal for whether a route
        // was planned or found. Never turned into a claim — planning is descriptive-only.
        corrections: outcome.backtracks,
      });

      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
